import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { TrendingDown, TrendingUp } from 'lucide';
import { NbIcon } from '../../icon/icon';
import { injectNbUiText } from '../../i18n/ui-text';

export type StatDeltaDir = 'up' | 'down';
/** KPI card background tone (2026-09 styre prototype: sand / sky / brand-soft). */
export type StatTone = 'none' | 'brand' | 'sand' | 'sky';

/**
 * `<nb-stat-card>` — KPI card: caption, big number, optional delta line, an
 * optional icon tile (`[nbStatIcon]`) and an optional sparkline
 * (`[nbStatSpark]`, use `<nb-sparkline>`). `tone` tints the whole card.
 *
 * The delta pairs a trending icon + a visually hidden "Opp:/Ned:" with the
 * colour, and the sparkline is decorative — the number is always text.
 *
 * @example
 * <nb-stat-card label="Åpne saker" value="4" delta="2 nye denne uken" tone="sand">
 *   <nb-medallion nbStatIcon variant="warning"><nb-icon [icon]="Wrench" /></nb-medallion>
 *   <nb-sparkline nbStatSpark [data]="[2,3,3,5,4,6,6,4]" tone="warning" />
 * </nb-stat-card>
 */
@Component({
  selector: 'nb-stat-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NbIcon],
  host: { '[class]': "'tone-' + tone()" },
  template: `<div class="top">
      <span class="lab">{{ label() }}</span>
      <ng-content select="[nbStatIcon]" />
    </div>
    <div class="row">
      <div>
        <div class="num">{{ value() }}</div>
        @if (delta()) {
          <div class="delta" [class]="deltaDir()" [class.neutral]="!deltaDir()">
            @if (deltaDir(); as dir) {
              <nb-icon [icon]="dir === 'down' ? TrendingDown : TrendingUp" />
              <span class="nb-sr-only">{{ dir === 'down' ? uiText().trendDown : uiText().trendUp }}:</span>
            }
            {{ delta() }}
          </div>
        }
      </div>
      <div class="spark"><ng-content select="[nbStatSpark]" /></div>
    </div>`,
  styleUrl: './stat-card.css',
})
export class NbStatCard {
  protected readonly uiText = injectNbUiText();
  /** The metric caption shown above the number (`.lab`). */
  readonly label = input.required<string>();
  /** The headline figure (`.num`). */
  readonly value = input.required<string>();
  /** Optional secondary line under the number (`.delta`). */
  readonly delta = input<string>();
  /** Trend direction — drives the colour and the trending icon of `delta`. Omit (undefined) for a neutral sub-line. */
  readonly deltaDir = input<StatDeltaDir | undefined>('up');
  /** Card background tone. */
  readonly tone = input<StatTone>('none');

  protected readonly TrendingUp = TrendingUp;
  protected readonly TrendingDown = TrendingDown;
}
